import moment from 'moment';
import { BILL_ALLOCATION_STATE } from 'src/api/billAutomation/bills';
import { resolveApPushInvoiceNumber } from 'src/pages/NewBillingModule/ApplyBillingFull/AppfolioPush';
import { isHeldByAppfolioPush } from './appfolioPushRow';
import { getUtilityKinds, OTHER_UTILITY_ICON, UTILITY_ICON_BY_KIND } from './BillDetails/resolveAllocationTargets';
const DASH = '—';
const TABLE_DATE_FORMAT = 'DD/MM/YY';

/** Where a bill entered the system, keyed by the service's BillSource values. */
export const BILL_SOURCES = {
  api: 'API',
  billsDashboard: 'Bills Dashboard',
  email: 'Email',
  integration: 'Integration',
  propertyAllocation: 'Property Allocation',
  scrape: 'Utility Portal'
};
const BILL_SOURCE_BY_API_VALUE = {
  Api: BILL_SOURCES.api,
  Email: BILL_SOURCES.email,
  Integration: BILL_SOURCES.integration,
  Manual: BILL_SOURCES.propertyAllocation,
  Portal: BILL_SOURCES.billsDashboard,
  Scrape: BILL_SOURCES.scrape
};

/**
 * Table labels for the service's BillStatus values, in pipeline order.
 *
 * The words are the PRD's rather than the service's: a bill that has been through validation is
 * "Processed" to the person reading the table, and one the pipeline could not place needs review.
 * Only the labels differ — every comparison goes through these keys, so the API's own values are
 * untouched.
 */
export const BILL_STATUSES = {
  pending: 'Pending',
  processing: 'Processing',
  extracted: 'Extracting',
  validated: 'Processed',
  needsProperty: 'Needs review',
  /**
   * Arrived at the company's bill-intake address from a sender we don't recognise. Deliberately not
   * called "Error": nothing went wrong, we declined it — and the PM can undo that by approving the
   * sender. "Rejected" is the word that invites that action.
   */
  rejected: 'Rejected',
  /** The same document the company already sent. Nothing to do; it's here so they can see we got it. */
  duplicate: 'Duplicate',
  failed: 'Error'
};
const BILL_STATUS_BY_API_VALUE = {
  Pending: BILL_STATUSES.pending,
  Processing: BILL_STATUSES.processing,
  Extracted: BILL_STATUSES.extracted,
  Validated: BILL_STATUSES.validated,
  NeedsProperty: BILL_STATUSES.needsProperty,
  Rejected: BILL_STATUSES.rejected,
  Duplicate: BILL_STATUSES.duplicate,
  Failed: BILL_STATUSES.failed
};

/**
 * The name each icon is filtered and labelled by: one for every icon this feature can draw — each of
 * the resolver's kinds, the combined water/sewer icon getUtilityIconForName returns, and the "other"
 * icon for a service it names no utility for. An icon drawn here without a name here reads as
 * "Other" in the filter, which is the same drift this change removes.
 */
const UTILITY_LABEL_BY_ICON = {
  cable: 'Cable/Satellite',
  electricity: 'Electricity',
  internet: 'Internet',
  landscaping: 'Landscaping',
  meter: 'Other',
  'natural-gas': 'Gas',
  sewer: 'Sewer',
  trash: 'Trash',
  water: 'Water',
  'water/sewer': 'Water/Sewer'
};

/**
 * The utilities column is icon-only, so the filter labels are derived from those icons. Going
 * through the icon keeps a bill's own wording for a service out of the filter's vocabulary.
 */
export function getUtilityLabel(iconType) {
  return UTILITY_LABEL_BY_ICON[iconType] || 'Other';
}

/**
 * The icons for the utilities a bill charges for. The bill names its services in its own words
 * ("electric", "Refuse Collection"), so they are read for the utility they name — the same reading
 * the allocation suggestion matches a property's utilities with. One utility billed twice, on two
 * properties or as two line groups, is one icon.
 *
 * A service covering two utilities is drawn as both, unlike getUtilityIconForName, which answers with
 * a single icon because it labels one tab. This column is a list of the utilities on the bill, so a
 * "Water/Sewer" service belongs in it twice — once under water and once under sewer.
 */
export const getBillUtilityIcons = utilities => [...new Set((utilities || []).flatMap(utility => {
  const kinds = getUtilityKinds(utility === null || utility === void 0 ? void 0 : utility.serviceType);
  return kinds.length ? kinds.map(kind => UTILITY_ICON_BY_KIND[kind] || OTHER_UTILITY_ICON) : [OTHER_UTILITY_ICON];
}))];

/**
 * The states in which a service already has an allocation bill behind it — approved, held for
 * review, or still being calculated. All three live in the allocation service, which is why a bill
 * with any of them is no longer the dashboard's to delete.
 */
const ALLOCATED_STATES = [BILL_ALLOCATION_STATE.allocating, BILL_ALLOCATION_STATE.awaitingApproval, BILL_ALLOCATION_STATE.needsReview, BILL_ALLOCATION_STATE.allocated, BILL_ALLOCATION_STATE.skipped];

/** The services of a bill that have been allocated. A service refused outright is not one of them. */
export const getAllocatedServices = utilities => (utilities || []).filter(utility => ALLOCATED_STATES.includes(utility === null || utility === void 0 ? void 0 : utility.allocationState));

/**
 * Why a bill cannot be deleted, or null when it can. The two holds are unrelated and a bill can be
 * under both, so they are named separately rather than collapsed into one apology — being told a bill
 * was allocated when what is actually holding it is a payable in AppFolio sends someone looking in the
 * wrong place.
 */
/**
 * Why the bill's details can no longer be edited. Same vocabulary as the delete guard on purpose — a
 * bill held by an allocation and one held by a payable are different problems with different fixes, and
 * naming the wrong one sends somebody looking in the wrong place.
 *
 * `isPushed` covers a push still in flight: `isHeldByAppfolioPush` counts `pending`, and an edit made
 * between the trigger and the answer is discarded without trace — the request already carried the
 * pre-edit data, and this side's edit is session-only.
 */
export const getEditBlockedReason = _ref => {
  let isAllocated = _ref.isAllocated,
    isPushed = _ref.isPushed;
  if (isAllocated && isPushed) {
    return 'This bill has been allocated and pushed to AppFolio, so its details can no longer be edited.';
  }
  if (isPushed) return 'This bill has been pushed to AppFolio, so its details can no longer be edited.';
  if (isAllocated) return 'This bill has been allocated, so its details can no longer be edited.';
  return null;
};
export const getDeleteBlockedReason = _ref2 => {
  let isAllocated = _ref2.isAllocated,
    isPushed = _ref2.isPushed;
  if (isAllocated && isPushed) {
    return 'This bill has been allocated and pushed to AppFolio, so it can no longer be deleted.';
  }
  if (isPushed) return 'This bill has been pushed to AppFolio and can no longer be deleted.';
  if (isAllocated) return 'This bill has been allocated and can no longer be deleted.';
  return null;
};
export const formatTableDate = value => {
  if (!value) return DASH;
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(TABLE_DATE_FORMAT) : DASH;
};

/**
 * Both ends of the period are billed, so 1–31 March is 31 days and not 30. Times of day are dropped
 * first, otherwise a period stamped at different hours loses or gains a day.
 */
export const getServiceDayCount = (start, end) => {
  if (!(start !== null && start !== void 0 && start.isValid()) || !(end !== null && end !== void 0 && end.isValid())) return DASH;
  return Math.max(end.clone().startOf('day').diff(start.clone().startOf('day'), 'days') + 1, 1);
};

/**
 * How long ago the bill arrived. The column is about recency rather than a date — "2 hours ago"
 * answers "is this new?" at a glance, which the timestamp does not. The exact time is kept on the
 * cell's tooltip for when it matters.
 */
export const getReceivedLabel = acquiredAt => {
  const at = acquiredAt ? moment(acquiredAt) : null;
  return at !== null && at !== void 0 && at.isValid() ? at.fromNow() : DASH;
};

/** An id rather than a person is no use in a table cell. */
const isGuid = value => /^[0-9a-f]{8}(-[0-9a-f]{4}){3}-[0-9a-f]{12}$/i.test(value || '');

/**
 * Who the bill came from: the sender's address when it arrived by email, otherwise the channel it
 * came through and, when the service names one, the person who put it there.
 */
export const getFromLabel = bill => {
  if (bill.senderEmail) return bill.senderEmail;
  const source = BILL_SOURCE_BY_API_VALUE[bill.source] || bill.source || '';
  const by = isGuid(bill.acquiredBy) ? '' : bill.acquiredBy;
  return [source, by].filter(Boolean).join(' · ') || DASH;
};

/** The address each property is filed under, for reading a bill's property ids back as addresses. */
export const getPropertyAddressIndex = properties => Object.fromEntries((properties || []).map(property => [property.propertyId, property.propertyAddress || property.propertyName || '']));

/**
 * Maps a bill from the company bills endpoint onto the shape the table renders. `addressById` names
 * the company's properties, so a bill matched to one is shown under the address it is filed under
 * rather than the address its provider printed — the two are rarely written the same way.
 */
export const mapCompanyBillToRow = (bill, addressById) => {
  var _bill$allocation$done, _bill$allocation, _bill$batchId, _bill$allocation$done2, _bill$allocation2, _bill$allocation$tota, _bill$allocation3;
  const serviceStart = bill.serviceStartDate ? moment(bill.serviceStartDate) : null;
  const serviceEnd = bill.serviceEndDate ? moment(bill.serviceEndDate) : null;
  const hasServicePeriod = (serviceStart === null || serviceStart === void 0 ? void 0 : serviceStart.isValid()) && (serviceEnd === null || serviceEnd === void 0 ? void 0 : serviceEnd.isValid());
  const utilityIcons = getBillUtilityIcons(bill.utilities);
  const propertyIds = bill.propertyIds || [];
  const filedAddresses = propertyIds.map(propertyId => addressById === null || addressById === void 0 ? void 0 : addressById[propertyId]).filter(Boolean);
  const allocated = getAllocatedServices(bill.utilities);
  const isAllocated = allocated.length > 0 || ((_bill$allocation$done = (_bill$allocation = bill.allocation) === null || _bill$allocation === void 0 ? void 0 : _bill$allocation.done) !== null && _bill$allocation$done !== void 0 ? _bill$allocation$done : 0) > 0;
  const isPushed = isHeldByAppfolioPush(bill.apPush);
  const status = BILL_STATUS_BY_API_VALUE[bill.extractStatus] || bill.extractStatus || BILL_STATUSES.pending;
  return {
    id: bill.billId,
    /** The acquisition event this bill arrived in (LB-5828), so the list can scope to one email/upload. */
    batchId: (_bill$batchId = bill.batchId) !== null && _bill$batchId !== void 0 ? _bill$batchId : null,
    /**
     * A bill is the dashboard's to delete until one of its services is allocated, in part or in full,
     * or an AppFolio payable exists for it. The service refuses either way; this only saves offering an
     * action it would turn down.
     */
    canDelete: !isAllocated && !isPushed,
    /** Carried on the row so every surface offering the delete says the same thing about why it cannot. */
    isAllocated,
    isPushed,
    deleteBlockedReason: getDeleteBlockedReason({
      isAllocated,
      isPushed
    }),
    fileName: bill.fileName || DASH,
    status,
    /**
     * The service's own word for where extraction got to. The AP push gates on it directly rather
     * than on the mapped label, so the check reads the same value the service does.
     */
    extractStatus: bill.extractStatus || '',
    /**
     * Why the bill is in this status, when the service says. The fallback is whatever label the
     * pill shows — the mapped one when the status is known, the raw API value when it is not — so
     * the hover can never contradict the pill: "Validated" over a pill that says "Processed" reads
     * as two different states.
     */
    statusTooltip: bill.statusReason || status,
    /** The bill a duplicate repeats, so the row can point at what we already hold. */
    duplicateOfBillId: bill.duplicateOfBillId || null,
    billSource: BILL_SOURCE_BY_API_VALUE[bill.source] || bill.source || DASH,
    allocation: {
      done: (_bill$allocation$done2 = (_bill$allocation2 = bill.allocation) === null || _bill$allocation2 === void 0 ? void 0 : _bill$allocation2.done) !== null && _bill$allocation$done2 !== void 0 ? _bill$allocation$done2 : 0,
      total: (_bill$allocation$tota = (_bill$allocation3 = bill.allocation) === null || _bill$allocation3 === void 0 ? void 0 : _bill$allocation3.total) !== null && _bill$allocation$tota !== void 0 ? _bill$allocation$tota : 0
    },
    /**
     * Where the document's AP push stands, read from the bill-automation ledger with the row — so the
     * table needs no status call of its own, and the list refresh is what settles a pending push.
     * Carries no attempts: the details view reads those for the one bill it shows.
     */
    apPush: bill.apPush || null,
    utilityIcons,
    utilityTypes: utilityIcons.map(getUtilityLabel),
    /** What the bill charges for, with the utility each was matched to, for the details view. */
    utilities: bill.utilities || [],
    /** Every property the bill is for, as the company knows them. */
    propertyIds,
    // A bill nobody has matched to a property yet has only what its provider printed to show.
    propertiesLines: filedAddresses.length ? filedAddresses : bill.properties || [],
    account: bill.accountNumber || DASH,
    provider: bill.provider || DASH,
    servicePeriod: hasServicePeriod ? "".concat(serviceStart.format(TABLE_DATE_FORMAT), " - ").concat(serviceEnd.format(TABLE_DATE_FORMAT)) : DASH,
    days: getServiceDayCount(serviceStart, serviceEnd),
    amount: typeof bill.amount === 'number' ? bill.amount : null,
    invoiceNumber: bill.invoiceNumber || DASH,
    /**
     * Whether this row's own numbers give the push an identity to file under. The list carries no parse
     * confidence and no per-property account numbers, so this is the bill-level reading — enough to keep
     * the table from offering a push the service is certain to refuse. The details view, which has the
     * whole extraction, judges the rest.
     */
    pushInvoiceNumber: resolveApPushInvoiceNumber({
      extraction: {
        accountNumber: bill.accountNumber,
        invoiceNumber: bill.invoiceNumber
      },
      typedInvoiceNumber: ''
    }),
    dueDate: formatTableDate(bill.dueDate),
    dateUploaded: formatTableDate(bill.acquiredAt),
    /** Raw, for the received-within filter and the exact time behind the relative label. */
    receivedAt: bill.acquiredAt || '',
    received: getReceivedLabel(bill.acquiredAt),
    from: getFromLabel(bill),
    /** Kept apart from `from` so the cell can tell an address from a channel and link it. */
    senderEmail: bill.senderEmail || ''
  };
};